/**
 * tRPC client for the frontend.
 *
 * The `AppRouter` type comes from `@fellesen/api` (workspace dependency).
 * Importing it gives the frontend full type knowledge of every backend
 * procedure: their inputs, outputs, and error shapes. If a backend route
 * changes signature, the corresponding `useQuery` / `useMutation` call
 * in any frontend file produces a TypeScript compile error immediately.
 *
 * Usage in a component:
 *
 *     const groups = trpc.group.list.useQuery();
 *     const create = trpc.group.create.useMutation();
 *     create.mutate({ name: "Felles husstand" });
 *
 * URL configuration:
 *   - Dev: VITE_PUBLIC_API_URL is unset → uses /trpc (Vite proxy forwards to :8787)
 *   - Prod: VITE_PUBLIC_API_URL=https://fellesen-api.<account>.workers.dev
 *           → frontend hits the Worker directly on its own subdomain
 *
 * Credentials: cookies are sent on every request via `credentials: "include"`.
 * The backend's CORS config allows the frontend origin.
 */

import type { AppRouter } from "@fellesen/api/router";
import { createTRPCReact, httpBatchLink } from "@trpc/react-query";

export const trpc = createTRPCReact<AppRouter>();

export function getApiBase(): string {
  // In dev, VITE_PUBLIC_API_URL is unset and we use a relative URL that
  // Vite's proxy forwards to localhost:8787.
  // In prod, the deploy script sets VITE_PUBLIC_API_URL at build time.
  const explicit = import.meta.env.VITE_PUBLIC_API_URL;
  if (explicit) return explicit.replace(/\/$/, ""); // strip trailing slash
  return ""; // relative — proxied in dev, won't work without proxy
}

/**
 * Build the tRPC client. Called once at app startup; the resulting client
 * is passed to <trpc.Provider>.
 */
export function buildTrpcClient() {
  return trpc.createClient({
    links: [
      httpBatchLink({
        url: `${getApiBase()}/trpc`,
        fetch(url, options) {
          // `RequestInit.signal` is typed as `AbortSignal | null` in the DOM
          // lib; the value tRPC hands us is `AbortSignal | undefined`. Coerce
          // here so the resulting object matches the lib type under
          // `exactOptionalPropertyTypes`.
          return fetch(url, {
            ...options,
            credentials: "include",
            signal: options?.signal ?? null,
          });
        },
      }),
    ],
  });
}

/**
 * URL helper for non-tRPC routes (Google OAuth redirect, PDF upload).
 * Use this instead of hardcoding paths so they work in both dev and prod.
 */
export function apiUrl(path: string): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${getApiBase()}${normalized}`;
}

/**
 * URL helper for WebSocket endpoints (`/ws/user`).
 *
 * Translates the http(s) API base into the matching ws(s) protocol so
 * the same routing works in dev (Vite proxy on :5173 / :5174) and prod
 * (Pages → Worker on the canonical frontend host). A relative base
 * resolves against `window.location`, which keeps cookies same-origin.
 */
export function wsUrl(path: string): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  const base = getApiBase();
  // No explicit API base: build a same-origin ws(s):// URL from the
  // current page. This is the dev + prod-with-proxy case.
  if (!base) {
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${protocol}//${window.location.host}${normalized}`;
  }
  // Explicit API base: rewrite scheme. Falls through unchanged if the
  // caller already passed ws(s)://.
  const wsBase = base.replace(/^https:\/\//, "wss://").replace(/^http:\/\//, "ws://");
  return `${wsBase}${normalized}`;
}
